import { api } from '../apiUtils';
import { FlaggedPhrase } from './contentScorer';
import { PatternHit } from './patternRules';

export type EditorType = 'webinar_campaign' | 'plx_campaign' | 'webinar_followup' | 'redlink';

export type UserDecision = 'accepted' | 'overridden' | 'modified_after_warn';

export interface CategoryScores {
  urgency: number;
  promise: number;
  authenticity: number;
  formatting: number;
}

export interface RewriteSuggestion {
  section: 'subject' | 'preheader' | 'body';
  current: string;
  suggested: string;
}

export interface LlmResponse {
  score: number;
  category_scores?: CategoryScores;
  flagged_phrases?: Array<{
    text: string;
    severity: 'red' | 'orange' | 'yellow';
    reason: string;
    suggestion: string;
  }>;
  rewrite_suggestions?: RewriteSuggestion[];
  overall_assessment?: string;
}

export interface SpamScoreResult {
  editor_type: EditorType;
  reference_id: number | null;
  content_hash: string;
  layer1_score: number;
  layer2_score: number | null;
  final_score: number;
  flagged_phrases: FlaggedPhrase[];
  pattern_hits: PatternHit[];
  red_count: number;
  orange_count: number;
  yellow_count: number;
  breakdown: {
    wordPenalty: number;
    patternPenalty: number;
    subjectLengthPenalty: number;
    linkDensityPenalty: number;
    imageOnlyPenalty: number;
  };
  llm_response: LlmResponse | null;
  from_cache: boolean;
  should_hard_block: boolean;
}

export interface CheckRequest {
  editor_type: EditorType;
  reference_id?: number | null;
  campaign_id?: number | null;
  subject: string;
  preheader: string;
  body: string;
  from_name?: string;
  include_llm?: boolean;
  force_refresh?: boolean;
}

export async function checkSpamScore(req: CheckRequest): Promise<SpamScoreResult> {
  const response = await api.post('/api/spam-score/check', req);
  if (!response.data?.success) {
    throw new Error(response.data?.error || 'Spam-Score-Check fehlgeschlagen');
  }
  return response.data.data as SpamScoreResult;
}

export async function recordDecision(
  editorType: EditorType,
  referenceId: number,
  decision: UserDecision,
  campaignId?: number | null,
): Promise<void> {
  await api.post('/api/spam-score/decision', {
    editor_type: editorType,
    reference_id: referenceId,
    campaign_id: campaignId ?? null,
    decision,
  });
}

export interface LatestScoreRecord {
  layer1_score: number;
  layer2_score: number | null;
  final_score: number;
  flagged_phrases: FlaggedPhrase[] | null;
  pattern_hits: PatternHit[] | null;
  llm_response: LlmResponse | null;
  user_decision: UserDecision | null;
  scored_by: string | null;
  scored_at: string;
  content_hash: string;
  campaign_id: number | null;
}

export async function fetchLatestScore(
  editorType: EditorType,
  referenceId: number,
  campaignId?: number | null,
): Promise<LatestScoreRecord | null> {
  const params = campaignId != null ? `?campaign_id=${encodeURIComponent(campaignId)}` : '';
  const response = await api.get(`/api/spam-score/latest/${editorType}/${referenceId}${params}`);
  if (!response.data?.success) return null;
  return response.data.data || null;
}

// Hash identical to backend computeContentHash (subject+preheader+body with
// NUL separators, sha256 hex). Used to verify whether a stored L2 result
// still matches the current editor content after page reload.
export async function computeContentHashClient(
  subject: string,
  preheader: string,
  body: string,
): Promise<string> {
  const normalized = `${(subject || '').trim()}\u0000${(preheader || '').trim()}\u0000${(body || '').trim()}`;
  const bytes = new TextEncoder().encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}
